import { fmtAgo } from './format';
import type { Lock, Organization } from './types';

/** Kleurgrenzen in het portaal (los van de e-maildrempel per club). */
export const BATTERY_RED_PCT = 15;
export const BATTERY_ORANGE_PCT = 30;
/** Standaard e-maildrempel (organizations.battery_alert_pct). */
export const DEFAULT_BATTERY_ALERT_PCT = 25;
/** Zonder gateway: na zoveel dagen zonder meting is de stand "oud". */
export const BATTERY_STALE_DAYS = 14;

export type BatteryLevel = 'unknown' | 'red' | 'orange' | 'ok';

export function batteryLevel(pct: number | null | undefined): BatteryLevel {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return 'unknown';
  if (pct <= BATTERY_RED_PCT) return 'red';
  if (pct <= BATTERY_ORANGE_PCT) return 'orange';
  return 'ok';
}

export interface BatteryInfo {
  level: BatteryLevel;
  /** "42%" of "Onbekend". */
  label: string;
  /** Korte actie bij rood/oranje, anders null. */
  advice: string | null;
  /** "gemeten 3 dagen geleden" / "nog niet gemeten". */
  measured: string;
  /** Meting ouder dan BATTERY_STALE_DAYS (stand kan nu lager zijn). */
  stale: boolean;
}

/** Moment waarop de batterij gemeten is (oudere sloten zonder battery_reported_at: last_seen_at). */
export function batteryMeasuredAt(lock: Pick<Lock, 'battery_reported_at' | 'last_seen_at'>): string | null {
  return lock.battery_reported_at ?? lock.last_seen_at ?? null;
}

export function batteryInfo(pct: number | null | undefined, measuredAt: string | null | undefined, now = new Date()): BatteryInfo {
  const level = batteryLevel(pct);
  const known = level !== 'unknown';
  return {
    level,
    label: known ? `${Math.round(pct!)}%` : 'Onbekend',
    advice: level === 'red' ? 'Vervang batterijen' : level === 'orange' ? 'Batterij raakt op' : null,
    measured: known && measuredAt ? `gemeten ${fmtAgo(measuredAt, now)}` : 'nog niet gemeten',
    stale: !!measuredAt && now.getTime() - new Date(measuredAt).getTime() > BATTERY_STALE_DAYS * 86400_000,
  };
}

export function alertThreshold(org: Pick<Organization, 'battery_alert_pct'> | null | undefined): number {
  return org?.battery_alert_pct ?? DEFAULT_BATTERY_ALERT_PCT;
}

/** Geïnstalleerde sloten met batterij op of onder de drempel, laagste eerst. */
export function lowBatteryLocks<T extends Pick<Lock, 'battery_pct' | 'status'>>(locks: T[], threshold: number): T[] {
  return locks
    .filter((l) => l.status === 'installed' && l.battery_pct !== null && l.battery_pct <= threshold)
    .sort((a, b) => a.battery_pct! - b.battery_pct!);
}
